import { createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

// 輔助函數：生成 23 小時的隨機趨勢資料 (從昨日 10:00 到今日 09:00)
const generateTrendSeries = () => {
  const series = [];
  const now = new Date();
  now.setHours(now.getHours(), 0, 0, 0); // 今日整點 (例如 09:00)

  // 生成 24 個點 (涵蓋 23 小時的跨度)
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 3600000);
    // 模擬 API：UTC 字串且不帶 Z (例如 2026-04-27T10:00:00)
    const utcStr = d.toISOString().replace("Z", "");
    const randomVal = Math.floor(Math.random() * 60) + 20; // 20~80s
    series.push([utcStr, randomVal]);
  }
  return series;
};

// 輔助函數：生成隨機分佈資料
const generateDistribution = () => {
  return [
    ["0-30", Math.floor(Math.random() * 200) + 50],
    ["30-60", Math.floor(Math.random() * 200) + 50],
    ["60-120", Math.floor(Math.random() * 200) + 50],
    ["120-150", Math.floor(Math.random() * 200) + 50],
    ["150-", Math.floor(Math.random() * 50) + 10],
  ];
};

const connectSuccessRate = {
  status: "ok",
  msg: "",
  data: {
    attempts: 1000,
    success: 743,
  },
};

const connectTime = {
  status: "ok",
  msg: "",
  data: {
    P95sec: 87,
    P50sec: 37,
    threshold: 120,
    sessions: 1000,
    series: generateTrendSeries(),
    distribution: generateDistribution(),
  },
};

const guestAccessHappiness = {
  status: "ok",
  msg: "",
  data: {
    sessions: 1000,
    series: [
      ["0-60", 180],
      ["60-600", 420],
      ["600-3600", 330],
      ["3600-", 100],
    ],
  },
};

const connectTimeDevices = {
  status: "ok",
  msg: "",
  data: {
    data: [
      { deviceName: "AP-Service-Counter-01", deviceId: uuidv4(), events: 842, P95sec: 28.4 },
      { deviceName: "AP-Executive-Suite-A", deviceId: uuidv4(), events: 156, P95sec: 22.1 },
      { deviceName: "AP-Hallway-East-03", deviceId: uuidv4(), events: 1420, P95sec: 18.7 },
      { deviceName: "AP-Parking-Gate-B", deviceId: uuidv4(), events: 98, P95sec: 14.2 },
      { deviceName: "AP-Training-Room-02", deviceId: uuidv4(), events: 2105, P95sec: 12.5 },
    ],
  },
};

const connectTimeSsids = {
  status: "ok",
  msg: "",
  data: {
    data: [
      { ssidName: "Public_Guest_Free", events: 4821, P95sec: 32.8 },
      { ssidName: "Warehouse_Inventory_Handheld", events: 945, P95sec: 24.5 },
      { ssidName: "Contractor_VPN_Access", events: 312, P95sec: 19.2 },
      { ssidName: "Office_Standard_Employee", events: 12450, P95sec: 11.4 },
      { ssidName: "Smart_Building_Sensors", events: 5600, P95sec: 8.2 },
    ],
  },
};

const connectSuccessRateDevices = {
  status: "ok",
  msg: "",
  data: {
    data: [
      { deviceName: "AP-Meeting-Room-04", deviceId: uuidv4(), attempts: 300, successRatePercent: 62.5 },
      { deviceName: "AP-Elevator-Lobby-B1", deviceId: uuidv4(), attempts: 200, successRatePercent: 68.2 },
      { deviceName: "AP-Open-Office-North-02", deviceId: uuidv4(), attempts: 250, successRatePercent: 74.1 },
      { deviceName: "AP-Cafe-Area-West", deviceId: uuidv4(), attempts: 150, successRatePercent: 77.5 },
      { deviceName: "AP-Warehouse-Dock-01", deviceId: uuidv4(), attempts: 100, successRatePercent: 79.8 },
    ],
  },
};

const connectSuccessRateSsids = {
  status: "ok",
  msg: "",
  data: {
    data: [
      { ssidName: "Corporate_Pro_5G", attempts: 400, successRatePercent: 99.4 },
      { ssidName: "Staff_Mobile_Internal", attempts: 200, successRatePercent: 96.8 },
      { ssidName: "Smart_Building_IoT", attempts: 150, successRatePercent: 92.1 },
      { ssidName: "Guest_Web_Portal", attempts: 150, successRatePercent: 84.5 },
      { ssidName: "Legacy_Scanner_2.4G", attempts: 100, successRatePercent: 71.2 },
    ],
  },
};

const guestAccessHappinessDevices = {
  status: "ok",
  msg: "",
  data: {
    data: [
      { deviceName: "AP-Service-Counter-01", deviceId: uuidv4(), sessions: 850, lessThan: { "60": { count: 357, percent: 42.0 } } },
      { deviceName: "AP-Executive-Suite-A", deviceId: uuidv4(), sessions: 1120, lessThan: { "60": { count: 414, percent: 37.0 } } },
      { deviceName: "AP-Hallway-East-03", deviceId: uuidv4(), sessions: 410, lessThan: { "60": { count: 135, percent: 32.9 } } },
      { deviceName: "AP-Parking-Gate-B", deviceId: uuidv4(), sessions: 2450, lessThan: { "60": { count: 686, percent: 28.0 } } },
      { deviceName: "AP-Training-Room-02", deviceId: uuidv4(), sessions: 1890, lessThan: { "60": { count: 435, percent: 23.0 } } },
    ],
  },
};

const guestAccessHappinessSsids = {
  status: "ok",
  msg: "",
  data: {
    data: [
      { ssidName: "Guest_Public_WiFi", sessions: 12450, lessThan: { "60": { count: 4856, percent: 39.0 } } },
      { ssidName: "Logistics_Scanner_2.4G", sessions: 3120, lessThan: { "60": { count: 967, percent: 31.0 } } },
      { ssidName: "IoT_Smart_Sensor_Net", sessions: 8900, lessThan: { "60": { count: 2225, percent: 25.0 } } },
      { ssidName: "Staff_Internal_Mobile", sessions: 15600, lessThan: { "60": { count: 2808, percent: 18.0 } } },
      { ssidName: "VIP_Executive_5G", sessions: 1200, lessThan: { "60": { count: 84, percent: 7.0 } } },
    ],
  },
};

const initialState = {
  connectSuccessRate,
  connectTime,
  guestAccessHappiness,
  connectTimeDevices,
  connectTimeSsids,
  connectSuccessRateDevices,
  connectSuccessRateSsids,
  guestAccessHappinessDevices,
  guestAccessHappinessSsids,
};

export const ainsightSlice = createSlice({
  name: "ainsightServiceExperience",
  initialState,
  reducers: {},
});

export default ainsightSlice.reducer;
